import { Alert, CircularProgress } from '@mui/material';
import { useDebounce } from 'usehooks-ts';
import MultiPageBuilder from 'components/MultiPageBuilder';
import PageLoader from 'components/PageLoader';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { numberToCurrencyString } from 'further-ui/utils';
import { round, sumBy } from 'lodash';
import { Dispatch, FC, SetStateAction } from 'react';
import { FieldArrayWithId } from 'react-hook-form';
import {
  CapitalActionFormValues,
  UpdateCapitalActionFormValues,
} from '../hooks/useCapitalActionForm';
import useGetEntryTitle from '../hooks/useGetEntryTitle';
import CapitalActionEntry from './CapitalActionEntry';
import useTotalCapitalContributionUsed from './useTotalCapitalContributionUsed';

type Props = {
  hasUncalledEqualisations: boolean;
  currentPageIndex: number;
  setCurrentPageIndex: Dispatch<SetStateAction<number>>;
  fields: Array<FieldArrayWithId<CapitalActionFormValues, 'entries'>>;
};

const CapitalActionEntries: FC<Props> = ({
  hasUncalledEqualisations,
  currentPageIndex,
  setCurrentPageIndex,
  fields,
}) => {
  const getEntryTitle = useGetEntryTitle();

  const { vehicle } = useLayoutContext();
  const {
    formMethods: { watch, formState },
  } = useFormWithStateContext<UpdateCapitalActionFormValues>();

  const capitalActionEntries = watch('entries') ?? [];
  const debouncedEntries = useDebounce(capitalActionEntries, 300);

  const { total: totalCapitalContributionUsed, isLoading: isTotalLoading } =
    useTotalCapitalContributionUsed({
      vehicleId: vehicle?._id ?? '',
      commitmentType: vehicle?.commitmentType,
      entries: debouncedEntries,
    });

  const totalAmount = round(
    sumBy(capitalActionEntries, (entry) =>
      [
        CapitalActionEntryType.CapitalRefund,
        CapitalActionEntryType.CapitalDistribution,
      ].includes(entry.entryType)
        ? -entry.totalAmountLcy
        : entry.totalAmountLcy,
    ) - totalCapitalContributionUsed,
    2,
  );

  if (!vehicle) {
    return <PageLoader />;
  }

  return (
    <>
      <MultiPageBuilder
        summaryHeader="Capital action summary"
        summaryFooterTitle="Total to be called / (distributed)"
        summaryFooterTotals={
          isTotalLoading ? (
            <CircularProgress size={16} />
          ) : (
            numberToCurrencyString(totalAmount, {
              currency: vehicle.baseCurrency,
              negativeInBrackets: true,
            })
          )
        }
        currentPageIndex={currentPageIndex}
        onPageChange={setCurrentPageIndex}
        defaultActivePageIndex={
          capitalActionEntries.length ? capitalActionEntries.length - 1 : 0
        }
        pageName="transaction"
      >
        {fields.map((field, index) => (
          <MultiPageBuilder.Page
            key={field.id}
            title={getEntryTitle(capitalActionEntries[index] ?? field)}
            totals={numberToCurrencyString(
              capitalActionEntries[index]?.totalAmountLcy ?? 0,
              {
                currency: vehicle.baseCurrency,
              },
            )}
          >
            <CapitalActionEntry
              hasUncalledEqualisations={hasUncalledEqualisations}
              index={index}
              vehicleId={vehicle._id}
              baseCurrency={vehicle.baseCurrency}
              hasDistributionEntry={capitalActionEntries.some(
                (e) =>
                  e.entryType === CapitalActionEntryType.CapitalDistribution,
              )}
              entriesCount={capitalActionEntries.length}
            />
          </MultiPageBuilder.Page>
        ))}
      </MultiPageBuilder>
      {!!formState.errors.entries?.message && (
        <Alert severity="error">{formState.errors.entries.message}</Alert>
      )}
    </>
  );
};

export default CapitalActionEntries;
